import {DOCUMENT} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, ParamMap} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import * as FileSaver from 'file-saver';
import {Subject} from 'rxjs';
import {delay, finalize, takeUntil} from 'rxjs/operators';
import {ProductEnum} from 'src/app/shared/enums';
import {
   AuthModel,
   CarrierOptionsRequest,
   ContextModel,
   OrderDetails,
   OrderResponse,
   Payment,
   PaymentUrlResponse,
   ServiceProviderResponseModel,
   ShippingAddress,
   Tariff,
} from 'src/app/shared/models';
import {ContactPersonModel} from 'src/app/shared/models/contact-person.model';
import {Price} from 'src/app/shared/models/price.model';
import {FeedbackService, OrderService, PaymentService} from 'src/app/shared/services';
import {AccountLinkGeneratorService} from 'src/app/shared/services/account-link-generator.service';
import {UserService} from 'src/app/shared/services/user.service';
import {OrdersService} from '../../../home/admin/services/orders.service';
import {buildFileName} from '../../../home/service-provider/orders-list/print/print.component';
import {ShippingAddressComponent} from '../shipping-address/shipping-address.component';
import {ShippingFinalComponent} from '../shipping-final/shipping-final.component';
import {ShippingOptionsComponent} from '../shipping-options/shipping-options.component';
import {PrintDeliveryDetailsCommand, ProductPrint} from '../../../shared/models/delivery-details-print-model';
import {Key} from 'protractor';

@Component({
   selector: 'app-shipping-steps',
   templateUrl: './shipping-steps.component.html',
   styleUrls: ['./shipping-steps.component.scss'],
})
export class ShippingStepsComponent implements OnInit, OnDestroy {
   idDelivery: any;
   @ViewChild(ShippingAddressComponent) shippingAddressComponent!: ShippingAddressComponent;
   @ViewChild(ShippingOptionsComponent) shippingOptionsComponent!: ShippingOptionsComponent;
   @ViewChild(ShippingFinalComponent) shippingFinalComponent!: ShippingFinalComponent;

   translatePath = 'public.shipping-steps.';
   private readonly contextKey = 'selectedContext';
   private readonly sessionStorageOptionKey = 'mdeliverysavedorderoption';
   private readonly sessionStorageAddressKey = 'mdeliverysavedorderaddress';
   _unsubscribe = new Subject();
   params!: ParamMap;
   orderDetails!: OrderDetails;
   orderResponse!: OrderResponse;
   isOrderInsertedAndPaid: boolean;
   success = true;
   isPaid = this.route.snapshot.queryParams.isPaid;
   isLocal: boolean;
   isConfirm: boolean = false;
   option: Price;
   shippingAddress!: ShippingAddress;
   serviceProviderResponse: ServiceProviderResponseModel;
   loading: boolean = false;
   paymentLoading: boolean = false;
   showNoData: boolean = false;
   errorMessage: string = '';
   user: AuthModel;
   isKnownError: boolean = true;
   orderId: string = '';
   serviceId: string = '';

   constructor(
      @Inject(DOCUMENT) private document: Document,
      public route: ActivatedRoute,
      public orderService: OrderService,
      public service: OrdersService,
      private userService: UserService,
      private accountLinkGenerator: AccountLinkGeneratorService,
      private translateService: TranslateService,
      private paymentService: PaymentService,
      private accountLinkGeneratorService: AccountLinkGeneratorService,
      private feedbackService: FeedbackService
   ) {}

   ngOnDestroy(): void {
      this._unsubscribe.next();
      this._unsubscribe.complete();
   }
   ngOnInit(): void {
      this.params = this.route.snapshot.queryParamMap;
      this.orderId = this.params.get('orderId')!;
      this.serviceId = this.params.get('serviceId')!;
      if (this.params.has('returUrl')) {
         const url = this.params.get('returUrl');
         localStorage.setItem('returUrl', JSON.stringify(url));
      }
      if (this.params.has('lang') && this.translateService.langs.includes(this.params.get('lang')!)) {
         this.translateService.use(this.params.get('lang')!);
      }
      if (this.params.has('mpass') && this.params.get('mpass')) {
         this.passiveLogin();
      } else {
         this.userService.isAutenticated$.subscribe((user) => {
            if (user?.isAuthenticated) {
               this.user = user;
               this.handleLoggedInUser();
            } else {
               this.getData();
            }
         });
      }
   }

   editModeEmitter(state: boolean) {
      console.log('state', state);
      this.shippingOptionsComponent.onEditModeStateChange(!state);
   }
   passiveLogin(): void {
      let successUrl = `${location.origin + location.pathname}?orderId=${
         this.params.has('orderId') ? this.params.get('orderId') : ''
      }&serviceId=${this.params.has('serviceId') ? this.params.get('serviceId') : ''}&lang=${
         this.params.has('lang') ? this.params.get('lang') : ''
      }&returUrl=${this.params.has('returUrl') ? this.params.get('returUrl') : ''}`;
      let failedUrl = `${location.origin + location.pathname}?orderId=${
         this.params.has('orderId') ? this.params.get('orderId') : ''
      }&serviceId=${this.params.has('serviceId') ? this.params.get('serviceId') : ''}&lang=${
         this.params.has('lang') ? this.params.get('lang') : ''
      }&returUrl=${this.params.has('returUrl') ? this.params.get('returUrl') : ''}`;
      location.replace(
         this.accountLinkGenerator.getPassiveUrl(
            successUrl,
            failedUrl,
            this.params.has('lang') ? this.params.get('lang') : '',
            this.params.has('returUrl') ? this.params.get('returUrl') : ''
         )
      );
   }
   handleLoggedInUser() {
      this.selectContext();
   }

   login() {
      this.accountLinkGenerator.getLoginUrl(location.href).subscribe((res) => {
         this.document.body.innerHTML = res;
         this.document.forms[0].submit();
      });
   }

   selectContext() {
      const selectedContext = localStorage.getItem(this.contextKey);
      if (selectedContext == null) {
         const auth = this.userService.getCurrentUser();
         const contexts = auth.user.boards as ContextModel[];
         this.userService.setContext(contexts[0]).subscribe((res) => {
            if (res.success) {
               this.getData();
            }
         });
      } else {
         this.getData();
      }
   }

   getData() {
      this.loading = true;
      this.showNoData = false;
      if (this.params.has('orderId')) {
         this.orderService
            .getOrder(this.params)
            .pipe(
               finalize(() => {
                  this.loading = false;
               }),
               takeUntil(this._unsubscribe)
            )
            .subscribe(
               (res) => {
                  this.orderResponse = res;
                  this.orderDetails = this.orderResponse.ticket;
                  this.serviceProviderResponse = this.orderResponse.serviceProvider;
                  this.isOrderInsertedAndPaid = this.serviceProviderResponse.isOrderInsertedAndPaid;
                  setTimeout(() => {
                     //this.shippingAddressComponent.patchSessionSavedData();
                     if (this.user?.isAuthenticated) {
                        // this.shippingAddressComponent.getSavedAddresses();
                     }
                  }, 0);
               },
               (err: HttpErrorResponse) => {
                  this.showNoData = true;
                  console.log(err);
                  if (typeof err.error !== 'string') {
                     this.isKnownError = false;
                     this.errorMessage = err.message;
                  } else {
                     this.errorMessage = err.error;
                  }
                  this.feedbackService.handleError(err);
               }
            );
      }
   }

   moveToSecondStep(event: {shippingAddress: ShippingAddress; autoConfirm: boolean; isLocal: boolean}) {
      this.shippingAddressComponent.valueBar = 11;
      this.isLocal = event.isLocal;
      this.shippingAddress = event.shippingAddress;
      let weight = 0;
      this.orderResponse.serviceProvider.products.forEach((item) => (weight += item.weight));
      let declaredAmount = 0;
      this.orderResponse.serviceProvider.products.forEach((item) => (declaredAmount += item.price));

      const request = {
         deliveryAddressDirection: null,
         priority: null,
         type: ProductEnum.Document,
         date: this.orderDetails.estimatedReadyAt,
         recipient: null,
         sender: null,
         serviceProviderIdno: this.orderResponse.serviceProvider.idno,
         recipientAddress: this.shippingAddress,
         senderAddress: this.serviceProviderResponse.pickupPoint.address,
         weight,
         items: this.orderResponse.serviceProvider.products,
         isLocal: event.isLocal,
         declaredAmount: declaredAmount,
         productType: this.orderResponse.serviceProvider.products[0]?.name,
      } as CarrierOptionsRequest;

      const soc = this.shippingOptionsComponent;
      soc.expanded = false;
      this.shippingFinalComponent.expanded = false;
      this.shippingAddressComponent.loading = true;

      setTimeout(() => {
         this.shippingAddressComponent.valueBar = 50;
      }, 100);

      soc.getOptions(request)
         .pipe(
            finalize(() => {
               this.shippingAddressComponent.valueBar = 100;
               setTimeout(() => {
                  if (this.shippingAddressComponent.valueBar === 100) {
                     this.shippingAddressComponent.valueChecking = true;
                  }
                  setTimeout(() => {
                     this.shippingAddressComponent.loading = false;
                     this.shippingAddressComponent.valueChecking = false;
                     this.shippingAddressComponent.valueBar = 30;
                     this.isConfirm = true;
                  }, 800);
               }, 390);
            })
         )
         .subscribe(
            (res) => {
               this.shippingAddressComponent.setEditModeState(false);
               soc.options = res;
               soc.expanded = true;
               if (!event.autoConfirm) {
                  sessionStorage.removeItem(this.sessionStorageOptionKey);
               }
               soc.sessionSavedOption = sessionStorage.getItem(this.sessionStorageOptionKey)
                  ? (JSON.parse(sessionStorage.getItem(this.sessionStorageOptionKey)!) as Tariff)
                  : null;
               const orderId = this.params.has('orderId') ? this.params.get('orderId') : null;
               const serviceId = this.params.has('serviceId') ? this.params.get('serviceId') : null;
               if (
                  orderId &&
                  serviceId &&
                  soc.sessionSavedOption &&
                  soc.sessionSavedOption.orderId == orderId &&
                  soc.sessionSavedOption.serviceId == serviceId
               ) {
                  soc.option = soc.sessionSavedOption.confirmedOption;
                  setTimeout(() => {
                     soc.confirmOption();
                     soc.setEditModeState(false);
                  }, 0);
               } else {
                  soc.option = null;
                  soc.confirmedOption = null;
                  soc.setEditModeState(true);
               }
               soc.createOptionsList();
               this.option = null as unknown as Price;
            },
            (err: HttpErrorResponse) => {
               this.feedbackService.handleError(err, this.shippingAddressComponent.addressForm);
            }
         );
   }

   newOptionSelected(event: any) {
      this.isConfirm = true;
      this.option = event;
      this.shippingOptionsComponent.setEditModeState(false);
      this.shippingFinalComponent.expanded = true;
   }

   pay(): void {
      let receiver: any = {};
      receiver = this.orderResponse.ticket.receiver;
      console.log(this.shippingAddress);
      this.shippingAddressComponent.saveNewAddress();

      this.shippingAddress.phone =
         this.shippingAddress.phone == null ||
         this.shippingAddress.phone == undefined ||
         this.shippingAddress.phone == ''
            ? '+373 600 00 000'
            : this.shippingAddress.phone;

      const estimatedDeliveryDays =
         (new Date(this.option.estimatedOnEnd).getTime() - new Date(this.option.estimatedOnStart).getTime()) /
         (1000 * 3600 * 24);
      !receiver.firstName ? (receiver.firstName = this.shippingAddress.firstName) : null;
      !receiver.name ? (receiver.name = this.shippingAddress.lastName) : null;
      !receiver.email ? (receiver.email = this.shippingAddress.email) : null;
      !receiver.phone ? (receiver.phone = this.shippingAddress.phone) : null;
      // receiver.phone === '060000000' ? receiver.phone = '+373 600 00 000' : receiver.phone = this.shippingAddress.phone;
      this.orderResponse.ticket.receiver.phone = this.shippingAddress.phone;
      let payment = {
         ticket: this.orderResponse.ticket,
         orderId: this.route.snapshot.queryParams.orderId,
         serviceId: this.orderResponse.serviceProvider.service.id,
         products: this.orderResponse.serviceProvider.products.map((item) => item.id),
         pickupPointId: this.orderResponse.serviceProvider.pickupPoint.id,
         carrierId: this.shippingOptionsComponent.getCarrierId(),
         carrierPrice: this.option.cost,
         cost: this.option.cost,
         deliveryType: this.option.deliveryType,
         contactPerson: {
            firstName: this.shippingAddress.firstName,
            lastName: this.shippingAddress.lastName,
            email:
               this.shippingAddress.email == null || this.shippingAddress.email == undefined
                  ? ''
                  : this.shippingAddress.email,
            phone: this.shippingAddress.phone,
         } as ContactPersonModel,
         address: this.option.addressOfDepartment ?? this.shippingAddress,
         estimatedDeliveryEnd: this.option.estimatedOnEnd,
         estimatedDeliveryStart: this.option.estimatedOnStart,
         estimatedDeliveryDays,
      } as Payment;

      console.log(payment);
      this.paymentLoading = true;
      this.paymentService
         .saveDelivery(payment, this.isLocal)
         .pipe(
            finalize(() => {
               this.paymentLoading = false;
            })
         )
         .subscribe(
            (res) => {
               this.idDelivery = res;
               if (this.idDelivery === 'Duplicate') {
                  this.loading = false;
                  this.feedbackService.handleInfo('This order was paid');
               } else {
                  const returUrl = this.route.snapshot.queryParams.returUrl;
                  console.log(returUrl);

                  this.paymentLoading = true;
                  console.log(res);

                  // console.log(this.orderResponse.serviceProvider.integrationType)
                  console.log(res);
                  if (this.orderResponse.serviceProvider.integrationType === 2 && this.isPaid != 'true') {
                     location.href = `${returUrl}?orderId=${this.orderResponse.ticket.id}&deliveryId=${this.idDelivery}&serviceId=${this.orderResponse?.serviceProvider.service.code}`;
                  }
               }
               if (this.orderResponse.serviceProvider.integrationType === 1 || this.isPaid == 'true') {
                  console.log(this.isPaid);

                  if (this.isPaid === undefined) {
                     this.isPaid = false;
                  }

                  if (this.idDelivery !== 'Duplicate') {
                     this.paymentService
                        .getPayData(res, this.isPaid)
                        .pipe(
                           finalize(() => {
                              this.paymentLoading = false;
                           })
                        )
                        .subscribe(
                           (res) => {
                              console.log(res);
                              // location.replace(res.payUrl)
                              this.payToMpay(res);
                              sessionStorage.removeItem(this.sessionStorageAddressKey);
                              sessionStorage.removeItem(this.sessionStorageOptionKey);
                           },
                           (err: HttpErrorResponse) => {
                              this.feedbackService.handleError(err);
                           }
                        );
                  }
               }
               // }
            },
            (err: HttpErrorResponse) => {
               this.loading = false;
               this.feedbackService.handleError(err);
            }
         );
   }

   payToMpay(object: PaymentUrlResponse) {
      console.log(object.serviceId);
      let returnUrl = `${location.origin}${this.accountLinkGeneratorService.getOrderConfirmationUrl(object.orderKey)}`;
      var param = {ServiceID: object.mPayServiceId, OrderKey: object.orderKey, ReturnUrl: returnUrl};
      this.post(object.payUrl, param, 'POST');
   }

   post(path: string, params: any, method: string) {
      method = method || 'post';
      var form = document.createElement('form');
      form.hidden = true;
      form.setAttribute('method', method);
      form.setAttribute('action', path);
      for (var key in params) {
         if (params.hasOwnProperty(key)) {
            var hiddenField = document.createElement('input');
            hiddenField.setAttribute('type', 'text');
            hiddenField.setAttribute('name', key);
            hiddenField.setAttribute('value', params[key]);
            form.appendChild(hiddenField);
         }
      }
      document.body.appendChild(form);
      form.submit();
   }

   shippment(e: boolean): any {}

   mapCountryName(countryName: string): string {
      var countryNameMap: {[Key: string]: string} = {
         'Republic of Moldova': 'Republica Moldova',
      };
      return countryNameMap[countryName] || countryName;
   }

   processShippingOptions(options: any[]) {
      var allCarrierDescriptions: string[] = [];
      var allCarrierPrices: number[] = [];
      var allCarrierEndDates: Date[] = [];
      var carrierMap: {[key: string]: {regularCost: number; expressCost: number; count: number}} = {};

      options.forEach((option) => {
         if (option.carrier.carrierName) {
            var currentCost = option.cost || 0;
            var currentCarrierName = option.carrier.carrierName;

            if (!carrierMap[currentCarrierName]) {
               carrierMap[currentCarrierName] = {regularCost: currentCost, expressCost: currentCost, count: 1};
            } else {
               if (currentCost < carrierMap[currentCarrierName].regularCost) {
                  carrierMap[currentCarrierName].regularCost = currentCost;
               }
               if (currentCost > carrierMap[currentCarrierName].expressCost) {
                  carrierMap[currentCarrierName].expressCost = currentCost;
               }
               carrierMap[currentCarrierName].count += 1;
            }
            allCarrierEndDates.push(option.estimatedOnEnd);
         }
      });

      for (const [carrierName, {regularCost, expressCost, count}] of Object.entries(carrierMap)) {
         if (count === 2 && regularCost > 1 && expressCost > 1) {
            allCarrierDescriptions.push(`${carrierName}`, `${carrierName} (Express)`);
            allCarrierPrices.push(regularCost, expressCost);
         } else if (count === 1 && regularCost > 1) {
            allCarrierDescriptions.push(carrierName);
            allCarrierPrices.push(regularCost);
         }
      }
      return {allCarrierDescriptions, allCarrierPrices, allCarrierEndDates};
   }

   printDeliveryDetails() {
      let printDeliveryDetailsCommand = new PrintDeliveryDetailsCommand();

      printDeliveryDetailsCommand.serviceProviderName = this.shippingFinalComponent.serviceProviderResponse.name;
      printDeliveryDetailsCommand.serviceProviderAddressStreet =
         this.shippingFinalComponent.serviceProviderResponse.pickupPoint.address?.street.value;
      printDeliveryDetailsCommand.serviceProviderAddressNumber =
         this.shippingFinalComponent.serviceProviderResponse.pickupPoint.address?.block;
      printDeliveryDetailsCommand.serviceProviderAddressAp =
         this.shippingFinalComponent.serviceProviderResponse.pickupPoint.address?.apartment;
      printDeliveryDetailsCommand.serviceProviderAddressRegion =
         this.shippingFinalComponent.serviceProviderResponse.pickupPoint.address?.region?.value;
      printDeliveryDetailsCommand.serviceProviderAddressZip =
         this.shippingFinalComponent.serviceProviderResponse.pickupPoint.address?.zipCode;

      printDeliveryDetailsCommand.firstName = this.shippingFinalComponent.shippingAddress?.firstName;
      printDeliveryDetailsCommand.lastName = this.shippingFinalComponent.shippingAddress?.lastName;
      printDeliveryDetailsCommand.email = this.shippingFinalComponent.shippingAddress?.email;
      printDeliveryDetailsCommand.phone = this.shippingFinalComponent.shippingAddress?.phone;

      printDeliveryDetailsCommand.appartament = this.shippingFinalComponent.shippingAddress?.apartment;
      printDeliveryDetailsCommand.block = this.shippingFinalComponent.shippingAddress?.block;
      printDeliveryDetailsCommand.cityName =
         this.shippingFinalComponent.shippingAddress.city?.value ?? this.shippingFinalComponent?.shippingAddress?.city;
      printDeliveryDetailsCommand.countryName =
         this.mapCountryName(this.shippingFinalComponent.shippingAddress?.country?.value) ??
         this.shippingFinalComponent?.shippingAddress?.country;
      printDeliveryDetailsCommand.regionName =
         this.shippingFinalComponent.shippingAddress?.region?.value ??
         this.shippingFinalComponent?.shippingAddress?.region;
      printDeliveryDetailsCommand.streetName =
         this.shippingFinalComponent.shippingAddress?.street?.value ??
         this.shippingFinalComponent?.shippingAddress?.street;
      printDeliveryDetailsCommand.provinceCode = this.shippingFinalComponent.shippingAddress?.provinceCode;
      printDeliveryDetailsCommand.zipCode = this.shippingFinalComponent.shippingAddress?.zipCode;

      if (this.shippingFinalComponent.option) {
         var oneCarrierDescriptions: string[] = [];
         var oneCarrierPrices: number[] = [];
         var oneCarrierEndDates: Date[] = [];

         if (this.shippingFinalComponent.option.carrier.carrierName) {
            oneCarrierDescriptions.push(this.shippingFinalComponent.option.carrier.carrierName);
            oneCarrierPrices.push(this.shippingFinalComponent.option.cost);
            oneCarrierEndDates.push(this.shippingFinalComponent.option.estimatedOnEnd);
         }

         var oneDescriptionsText = oneCarrierDescriptions;
         var onePricesText = oneCarrierPrices;
         var oneDatesText = oneCarrierEndDates;

         printDeliveryDetailsCommand.carrierName = oneDescriptionsText;
         printDeliveryDetailsCommand.carrierEndDate = oneDatesText;
         printDeliveryDetailsCommand.carrierPrice = onePricesText;
      } else {
         var {allCarrierDescriptions, allCarrierPrices, allCarrierEndDates} = this.processShippingOptions(
            this.shippingOptionsComponent.filterNormalizedOptions
         );
         var filteredCarrierEndDates = allCarrierEndDates.filter((date) => date !== null);
         var allDescriptionsText = allCarrierDescriptions;
         var allPricesText = allCarrierPrices;
         var allDatesText = filteredCarrierEndDates;

         printDeliveryDetailsCommand.carrierName = allDescriptionsText;
         printDeliveryDetailsCommand.carrierEndDate = allDatesText;
         printDeliveryDetailsCommand.carrierPrice = allPricesText;
      }

      this.shippingFinalComponent.serviceProviderResponse.products.forEach((item) => {
         let product = new ProductPrint();
         product.name = item.name;
         product.price = item.price;
         product.code = item.code;
         printDeliveryDetailsCommand.products.push(product);
      });
      this.isConfirm = false;
      this.service
         .deliveryDetailstoPdf(printDeliveryDetailsCommand)
         .pipe(
            delay(3000),
            finalize(() => {
               this.loading = false;
               this.isConfirm = true;
            })
         )
         .subscribe(
            (res: any) => {
               FileSaver.saveAs(res, buildFileName('deliveryDetails', 'pdf'));
               this.feedbackService.handleSuccess('success');
            },
            (error) => {
               this.feedbackService.handleWarning('server-error');
            }
         );
   }
}
